import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import {
  ProductionSceneNoteResource,
  ProductionSceneResource,
} from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";

export type FormDataT = {
  title: string;
  content: string;
  production_scene_id: number;
};

interface Props {
  onClose: () => void;
  scenes?: number[];
  note?: ProductionSceneNoteResource;
  scene?: ProductionSceneResource;
}

const SceneNotesModal: FC<Props> = ({ onClose, note, scenes, scene }) => {
  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: note?.title ?? "",
      content: note?.content ?? "",
      production_scene_id: scene?.id,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (note) {
          await api.put<never, { id: number }>(
            `/v1/production/scenes/notes/${note.id}`,
            data
          );
        } else {
          await api.post<never, ProductionSceneNoteResource>(
            "/v1/production/scenes/notes",
            data
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Note has been successfully ${!!note ? "updated" : "created"}
          `
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!note ? "Add" : "Edit"} scene note`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="title"
                label="Title"
                id="title"
              />
              {!!scenes?.length && (
                <FormField<FormDataT, number>
                  control={control}
                  name="production_scene_id"
                  label="Scene number"
                  id="scene_id"
                  type="select"
                  options={scenes.map((scene, index) => {
                    return {
                      label: (index + 1).toString(),
                      value: scene,
                    };
                  })}
                />
              )}
              <FormField<FormDataT>
                control={control}
                type="textarea"
                name="content"
                label="Note"
                id="note"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default SceneNotesModal;
